import { useEffect, useRef, useCallback } from 'react';
import { useSessionStore } from '../store/sessionStore';
import type { FrontendMessage } from '../utils/eventSchema';

const WS_URL = 'ws://localhost:8787';
const BACKOFF_STEPS = [1, 2, 4, 8, 16, 32, 60]; // seconds

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addEvent, addAnomaly, setActiveSession, setConnected } = useSessionStore();

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnected(false, 'connecting');

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true, 'live');
      reconnectAttempt.current = 0;
    };

    ws.onmessage = (ev) => {
      let msg: FrontendMessage;
      try {
        msg = JSON.parse(ev.data as string) as FrontendMessage;
      } catch {
        return;
      }

      switch (msg.type) {
        case 'event':
          addEvent(msg.data);
          break;
        case 'anomaly':
          addAnomaly(msg.data);
          break;
        case 'session_info':
          setActiveSession(msg.data);
          break;
        case 'session_ended':
          // handled by store if needed
          break;
      }
    };

    ws.onclose = () => {
      setConnected(false, 'disconnected');
      scheduleReconnect();
    };

    ws.onerror = () => {
      setConnected(false, 'disconnected');
    };
  }, [addEvent, addAnomaly, setActiveSession, setConnected]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    const delay = BACKOFF_STEPS[Math.min(reconnectAttempt.current, BACKOFF_STEPS.length - 1)];
    setConnected(false, 'reconnecting');
    reconnectAttempt.current++;
    reconnectTimer.current = setTimeout(connect, delay * 1000);
  }, [connect, setConnected]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    reconnectAttempt.current = 0;
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { connect, disconnect };
}
